/* import __COLOCATED_TEMPLATE__ from './filter.hbs'; */
/* RESPONSIBLE TEAM: team-standalone */

import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import type ImportSource from 'embercom/models/import-service/import-source';
import { SourceType } from 'embercom/models/import-service/import-source';
import { task } from 'ember-concurrency-decorators';
import { taskFor } from 'ember-concurrency-ts';
import type KnowledgeHubService from 'embercom/services/knowledge-hub-service';
import { type InterfaceIconName } from '@intercom/pulse/lib/interface-icons';
import type IntlService from 'ember-intl/services/intl';
import { type TaskGenerator } from 'ember-concurrency';
import type Store from '@ember-data/store';
// @ts-ignore
import { copy } from 'ember-copy';
import { type DataCategoryFilter } from 'embercom/components/knowledge-hub/salesforce-article-sync/data-category-editor';

export enum Operator {
  Equal = '=',
  NotEqual = '!=',
  GreaterThan = '>',
  LessThan = '<',
  GreaterThanOrEqual = '>=',
  LessThanOrEqual = '<=',
  Like = 'LIKE',
  NotLike = 'NOT LIKE',
}

export type DataType = 'string' | 'number' | 'boolean' | 'datetime';

export interface Predicate {
  name: string;
  type: DataType;
  operator: Operator;
  value: string | number | boolean;
}

export interface Filter {
  icon: InterfaceIconName;
  isDisabled: boolean;
  name: string;
  text: string;
  predicate: Predicate;
  type: string;
}

export type DataCategoryGroupList = {
  heading: string;
  items: {
    text: string;
    group: string;
  }[];
};

export type ArticleImportMode = 'all' | 'filtered';

interface Args {
  nextStep: () => void;
  previousStep: () => void;
  sourceId?: number;
  importSource?: ImportSource;
  setImportSource?: (importSource: ImportSource) => void;
  toggleSideDrawer: () => void;
}

export default class SalesforceArticleSyncStepFilter extends Component<Args> {
  @service declare appService: AppService;
  @service declare finStandaloneService: FinStandaloneService;
  @service declare knowledgeHubService: KnowledgeHubService;
  @service declare intl: IntlService;
  @service declare notificationsService: $TSFixMe;
  @service declare store: Store;

  @tracked articleImportMode: ArticleImportMode = 'all';
  @tracked importSource?: ImportSource;
  @tracked predicateGroup: $TSFixMe;
  @tracked dataCategoryGroupList: DataCategoryGroupList[] = [];
  @tracked dataCategoryFilter = {
    filter_type: 'AT',
    include: true,
    data_categories: {},
  } as DataCategoryFilter;

  private cachedPredicates: any[] | null = null;
  private isAdvancingStep = false;

  constructor(owner: any, args: Args) {
    super(owner, args);
    this.initialize();
  }

  private async initialize() {
    this.predicateGroup = this.store.createFragment('predicates/predicate-group');
    await taskFor(this.loadImportSourcesTask).perform();
  }

  get isLoadingImportSource() {
    return taskFor(this.loadImportSourcesTask).isRunning;
  }

  get isUpdatingPredicates() {
    return taskFor(this.updatePredicates).isRunning;
  }

  get showLoading() {
    return this.isLoadingImportSource || this.isUpdatingPredicates;
  }

  get showFilterSelection() {
    return this.articleImportMode === 'filtered';
  }

  get salesforceContentCountError() {
    return this.importSource?.remoteContentCountError;
  }

  get salesforceContentCount() {
    return this.importSource?.remoteContentCount || 0;
  }

  willDestroy() {
    super.willDestroy();
    if (this.cachedPredicates && this.importSource && !this.isAdvancingStep) {
      this.predicateGroup.set('predicates', this.cachedPredicates);
      this.knowledgeHubService
        .updateImportSourceWithParam(this.importSource.id, {
          predicate_group: this.predicateGroup.serialize(),
          data_category_filter: this.dataCategoryFilter,
        })
        .catch(() => {});
    }
  }

  @action
  advanceToNextStep() {
    this.isAdvancingStep = true;
    this.args.nextStep();
  }

  @action
  setArticleImportMode(mode: ArticleImportMode) {
    this.articleImportMode = mode;
    if (mode === 'all') {
      this.cachedPredicates = copy(this.predicateGroup.predicates);
      this.predicateGroup.set('predicates', []);
    } else if (mode === 'filtered' && this.cachedPredicates) {
      this.predicateGroup.set('predicates', this.cachedPredicates);
      this.cachedPredicates = null;
    }
    taskFor(this.updatePredicates).perform();
  }

  @action
  onUpdatePredicates(predicates: any) {
    this.predicateGroup?.set('predicates', copy(predicates));

    if (!this.predicateGroup.isValid) {
      return;
    }

    taskFor(this.updatePredicates).perform();
  }

  @action
  onUpdateDataCategoryFilter(dataCategoryFilter: any) {
    this.dataCategoryFilter = dataCategoryFilter;
    taskFor(this.updatePredicates).perform();
  }

  get isHelperTextError() {
    return !this.showLoading && this.salesforceContentCount === 0;
  }

  get isContinueDisabled() {
    return this.showLoading || this.salesforceContentCount === 0;
  }

  private hydrateFiltersFromSource() {
    let predicates = this.importSource?.context?.predicate_group?.predicates || [];
    this.predicateGroup.set('predicates', copy(predicates));
  }

  private hydrateDataCategoriesFromSource() {
    if (this.importSource?.metadata?.firstObject.data_categories) {
      this.dataCategoryGroupList = this.transformCategories(
        this.importSource?.metadata?.firstObject.data_categories,
      );
    }

    if (this.importSource?.context?.data_category_filter) {
      this.dataCategoryFilter = this.importSource?.context?.data_category_filter;
    }
  }

  private transformCategories(dataCategoryFilter: any) {
    let groupList: any[] = [];
    Object.keys(dataCategoryFilter).forEach((category) => {
      groupList.push({
        heading: category,
        items: dataCategoryFilter[category].map((item: any) => {
          return {
            text: item,
            group: category,
          };
        }),
      });
    });
    return groupList;
  }

  @task({ restartable: true })
  *updatePredicates(): TaskGenerator<void> {
    if (!this.importSource) {
      return;
    }

    yield this.knowledgeHubService.updateImportSourceWithParam(this.importSource.id, {
      predicate_group: this.predicateGroup.serialize(),
      data_category_filter: this.dataCategoryFilter,
    });
    this.importSource = yield this.importSource.reload();
  }

  @task({ drop: true })
  *loadImportSourcesTask(): TaskGenerator<void> {
    // Use the import source passed from step-wrapper if available
    if (this.args.importSource) {
      this.importSource = this.args.importSource;
      yield this.importSource.reload();
      this.hydrateFiltersFromSource();
      this.hydrateDataCategoriesFromSource();
      this.setInitialArticleImportMode();
      return;
    }

    // Otherwise, try to find by sourceId first
    if (this.args.sourceId) {
      this.importSource = yield this.store.findRecord(
        'import-service/import-source',
        Number(this.args.sourceId),
      );
    }

    if (this.importSource) {
      yield this.importSource.reload();
      this.hydrateFiltersFromSource();
      this.setInitialArticleImportMode();
    } else {
      let sources = yield this.knowledgeHubService.fetchImportSources();
      let sfSource = sources.find(
        (source: ImportSource) => source.sourceType === SourceType.Salesforce,
      );

      if (sfSource) {
        this.importSource = yield sfSource.reload();
      } else {
        try {
          this.importSource = yield this.knowledgeHubService.createImportSource({
            sourceType: SourceType.Salesforce,
          });
        } catch (error) {
          if (this.isSalesforceMetaDataError(error)) {
            this.notificationsService.notifyError(
              this.intl.t('standalone.salesforce.setup.article-sync.metadata-error'),
            );
          }
        }
      }
    }
    this.hydrateDataCategoriesFromSource();
    this.setInitialArticleImportMode();
  }

  private setInitialArticleImportMode() {
    let hasPredicates = (this.importSource?.context?.predicate_group?.predicates?.length ?? 0) > 0;
    let hasDataCategoryFilter =
      this.importSource?.context?.data_category_filter &&
      Object.keys(this.importSource.context.data_category_filter.data_categories || {}).length > 0;
    if (hasPredicates || hasDataCategoryFilter) {
      this.articleImportMode = 'filtered';
    }
  }

  private isSalesforceMetaDataError(error: any): boolean {
    let payload = error.jqXHR?.responseJSON;
    return (
      payload &&
      payload.error?.type === 'MetadataNotFoundError' &&
      payload.error?.source === 'salesforce'
    );
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'KnowledgeHub::SalesforceArticleSync::Steps::Filter': typeof SalesforceArticleSyncStepFilter;
    'knowledge-hub/salesforce-article-sync/steps/filter': typeof SalesforceArticleSyncStepFilter;
  }
}
